import { useEffect, useState } from 'react';
import { previewPastedLeads, type PastePayload } from '../lib/api';
import { dataTypeLabels, statusLabels, type PastePreviewResult } from '../lib/types';

const fields = [
  ['fullName', 'Name'], ['email', 'Email'], ['phone', 'Phone / Number'],
  ['website', 'Website'], ['company', 'Company'], ['title', 'Designation'],
  ['linkedinUrl', 'LinkedIn'], ['country', 'Country'], ['city', 'City'],
  ['state', 'State'], ['street', 'Street'], ['postalCode', 'ZIP / Postal code'],
  ['timeZone', 'Time zone'], ['employeeSize', 'Employee size'], ['industry', 'Industry'],
  ['uploadDate', 'Upload date'], ['sentDate', 'Sent date']
] as const;

export function PasteLeadPreview({ text, defaultStatus, campaignId, dataType, defaultCountry }: PastePayload) {
  const key = JSON.stringify({ text, defaultStatus, campaignId, dataType, defaultCountry });
  const [result, setResult] = useState<{ key: string; data?: PastePreviewResult; error?: string }>();
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    let active = true;
    setSelected(0);
    if (text.trim().length < 5) return;
    const timer = window.setTimeout(() => {
      void previewPastedLeads({ text, defaultStatus, campaignId, dataType, defaultCountry }).then(
        (data) => { if (active) setResult({ key, data }); },
        (error: unknown) => {
          if (active) setResult({ key, error: error instanceof Error ? error.message : 'Preview failed.' });
        }
      );
    }, 450);
    return () => { active = false; window.clearTimeout(timer); };
  }, [key, text, defaultStatus, campaignId, dataType, defaultCountry]);

  if (text.trim().length < 5) return null;
  const current = result?.key === key ? result : undefined;
  const row = current?.data?.rows[selected];
  const conflictSummary = current?.data?.conflictSummary;

  return (
    <div className="paste-preview" aria-label="Detected fields">
      <div className="panel-heading">
        <h3>Detected fields</h3>
        <span role="status">{current?.data ? `${current.data.totalRows} ${current.data.totalRows === 1 ? 'contact' : 'contacts'} detected` : current?.error || 'Checking fields...'}</span>
      </div>
      {conflictSummary && (
        <div className="import-conflict-grid" aria-label="Database match summary">
          {[
            ['New', conflictSummary.newEmails],
            ['Already in DB', conflictSummary.existingEmails],
            ['Positive protected', conflictSummary.protectedPositive],
            ['Negative protected', conflictSummary.protectedNegative],
            ['Failed protected', conflictSummary.protectedFailed],
            ['Suppressed', conflictSummary.suppressed],
            ['Duplicates in paste', conflictSummary.duplicateEmails],
            ['Invalid blocks', conflictSummary.invalidBlocks]
          ].map(([label, value]) => (
            <div className="import-conflict-card" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      )}
      {current?.data?.issues.map((issue, index) => (
        <div className="notice compact-notice" key={`${issue.line}-${index}`}>Line {issue.line}: {issue.message}</div>
      ))}
      {(current?.data?.rows.length ?? 0) > 1 && (
        <label>Contact
          <select value={selected} onChange={(event) => setSelected(Number(event.target.value))}>
            {current!.data!.rows.map((lead, index) => <option key={index} value={index}>{index + 1}. {lead.email}</option>)}
          </select>
        </label>
      )}
      {current?.data && current.data.totalRows > current.data.rows.length && <p>Preview: first 100 contacts.</p>}
      {row && (
        <dl className="paste-field-grid">
          {fields.map(([field, label]) => <div key={field}><dt>{label}</dt><dd>{row[field] || '-'}</dd></div>)}
          <div><dt>Status</dt><dd>{statusLabels[row.status] ?? row.status}</dd></div>
          <div><dt>Data type</dt><dd>{dataTypeLabels[row.dataType] ?? 'Other'}</dd></div>
          {Object.entries(row.customFields).map(([field, value]) => (
            <div key={`custom-${field}`}><dt>{field} (Custom)</dt><dd>{String(value)}</dd></div>
          ))}
        </dl>
      )}
    </div>
  );
}
